import ls from 'localstorage-slim';
import { StorageConfig } from 'localstorage-slim/dist/types';
import { useCallback, useState } from 'react';

import { usePreviewSettings } from '../../providers/PreviewProvider';
import { isSSR } from '../../utils/helpers';
import { logs } from '../../utils/logs';
import { useEffectOnce } from '../useEffectOnce';

export const useLocalStorageState = <T>(
  key: string,
  initialValue?: T,
  options?: StorageConfig
): [
  T | undefined,
  (value: T | ((val: T | undefined) => T)) => void,
  boolean,
] => {
  const previewSettings = usePreviewSettings();
  const getParsedOptions = useCallback(
    (): StorageConfig => ({
      // 2 minutes
      ttl: 2 * 60,
      ...options,
    }),
    [options]
  );
  const [localStorageInitialized, setLocalStorageInitialized] = useState(false);

  const readValueFromLocalStorage = useCallback(() => {
    const item: T | null = previewSettings.draftMode
      ? null
      : ls.get(key, getParsedOptions());
    return item;
  }, [previewSettings.draftMode, key, getParsedOptions]);

  const [storedValue, setStoredValue] = useState<T | undefined>(initialValue);

  useEffectOnce(() => {
    const valueFromLocalStorage = readValueFromLocalStorage();

    if (
      initialValue &&
      !previewSettings.draftMode &&
      valueFromLocalStorage === null
    ) {
      // If there's no value in localStorage, set the initialValue to localStorage
      ls.set(key, initialValue, getParsedOptions());
    }

    setStoredValue(valueFromLocalStorage ?? initialValue);
    setLocalStorageInitialized(true);
    // we only want this useEffect to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage.
  const setValue = (value: T | ((val: T | undefined) => T)) => {
    try {
      // Save to local storage and set state
      if (!isSSR()) {
        setStoredValue((prev) => {
          const newValue = value instanceof Function ? value(prev) : value;

          // Save the new value to local storage
          if (!previewSettings.draftMode) {
            ls.set(key, newValue, getParsedOptions());
          }
          return newValue;
        });
      }
    } catch (error) {
      logs.warning(
        {
          message: `Error setting localStorage key “${key}”`,
          localStorage: {
            key,
            options: getParsedOptions(),
          },
          error,
        },
        'LOCAL_STORAGE_WRITE_ERROR'
      );
    }
  };

  return [storedValue, setValue, localStorageInitialized];
};
